'use client';

import { useTranslations } from 'next-intl';
import {
  FINDING_CVSS_VERSIONS,
  formatFindingCvssPillScore,
  getFindingCvssDisplayValues,
  resolveFindingCvssVersionRecord,
  type CvssScoreStatus,
  type FindingCvssVersionSource,
} from '@terra/shared-cvss';
import { Badge } from '../../components/badge';
import { Text } from '../../components/text';
import { Tooltip } from '../../components/tooltip';

export type FindingCvssScoresPillProps = {
  finding: FindingCvssVersionSource & {
    cvssScoreStatus?: CvssScoreStatus | null;
  };
};

export function FindingCvssScoresPill({ finding }: FindingCvssScoresPillProps) {
  const t = useTranslations('designSystem.findings.cvssVersions');

  if (finding.cvssScoreStatus === 'PENDING') {
    return (
      <Badge variant="outline" size="sm">
        {t('calculating')}
      </Badge>
    );
  }

  const entries = FINDING_CVSS_VERSIONS.flatMap((version) => {
    const record = resolveFindingCvssVersionRecord({ finding, version });
    const score = formatFindingCvssPillScore(record);
    const vector = getFindingCvssDisplayValues(record).vector ?? '';

    return score ? [{ version, score, vector }] : [];
  });

  if (entries.length === 0) {
    return null;
  }

  return (
    <Tooltip
      whiteSpace="pre-line"
      title={entries
        .map(({ vector }) => vector)
        .filter((vector) => vector.length > 0)
        .join('\n')}
    >
      <Badge variant="outline" size="sm">
        <Text as="span">
          {entries.map(({ version, score }, index) => (
            <Text as="span" key={version}>
              {index > 0 ? ' / ' : null}
              <Text as="span" fontWeight="bold">
                {t('versionTitle', { version })}:
              </Text>
              {` ${score}`}
            </Text>
          ))}
        </Text>
      </Badge>
    </Tooltip>
  );
}
